import { useState } from 'react';
import type { FormEvent } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BlockEntry, BlockList, BlockMatchList, BlockTestResult } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useCan } from '../../lib/session';
import { useListState } from '../../lib/useList';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import {
  Alert,
  Badge,
  Button,
  Card,
  CheckboxField,
  ConfirmButton,
  EmptyRow,
  FilterBar,
  PageHeader,
  Pager,
  SelectField,
  Spinner,
  TableWrap,
  TextField,
  Th,
} from '../../components/ui';
import { Dialog } from '../fields-notices/Dialog';
import { SAFETY, formatDay, useSiteOptions } from './api';

const PAGE_SIZE = 20;
type Status = 'active' | 'inactive' | 'all';

interface Draft {
  fullName: string;
  aliases: string;
  company: string;
  criteria: string;
  allSites: boolean;
  siteIds: string[];
  expiresOn: string;
}

const blank: Draft = {
  fullName: '',
  aliases: '',
  company: '',
  criteria: '',
  allSites: true,
  siteIds: [],
  expiresOn: '',
};

function toDraft(e: BlockEntry): Draft {
  return {
    fullName: e.fullName,
    aliases: e.aliases.join('\n'),
    company: e.company,
    criteria: e.criteria,
    allSites: e.siteIds === null,
    siteIds: e.siteIds ?? [],
    expiresOn: e.expiresOn ?? '',
  };
}

/** One alias per line, blanks dropped. */
export function parseAliases(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function EntryDialog({ entry, onClose }: { entry: BlockEntry | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const sites = useSiteOptions();
  const [draft, setDraft] = useState<Draft>(entry ? toDraft(entry) : blank);
  const [localError, setLocalError] = useState('');
  const save = useMutation({
    mutationFn: () => {
      const body = {
        fullName: draft.fullName,
        aliases: parseAliases(draft.aliases),
        company: draft.company,
        criteria: draft.criteria,
        siteIds: draft.allSites ? null : draft.siteIds,
        expiresOn: draft.expiresOn || null,
      };
      return entry
        ? api.patch(`${SAFETY}/entries/${entry.id}`, {
            body,
            schema: BlockEntry,
          })
        : api.post(`${SAFETY}/entries`, { body, schema: BlockEntry });
    },
    onSuccess: async () => {
      toast(t('common.saved'));
      await qc.invalidateQueries({ queryKey: ['w18', 'block'] });
      onClose();
    },
  });
  const errors = fieldErrors(save.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setLocalError('');
    if (!draft.allSites && draft.siteIds.length === 0) {
      setLocalError(t('w18.block.pickSite'));
      return;
    }
    save.mutate();
  };
  return (
    <Dialog title={entry ? t('w18.block.edit') : t('w18.block.add')} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <TextField
          label={t('w18.block.name')}
          required
          maxLength={120}
          value={draft.fullName}
          error={errors['fullName']}
          onChange={(e) => setDraft({ ...draft, fullName: e.target.value })}
        />
        <div>
          <label htmlFor="w18-aliases" className="mb-1 block text-sm font-medium text-slate-800">
            {t('w18.block.aliases')}
          </label>
          <textarea
            id="w18-aliases"
            rows={3}
            className="block w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
            value={draft.aliases}
            aria-describedby="w18-aliases-hint"
            onChange={(e) => setDraft({ ...draft, aliases: e.target.value })}
          />
          <p id="w18-aliases-hint" className="mt-1 text-xs text-slate-600">
            {t('w18.block.aliasesHint')}
          </p>
        </div>
        <TextField label={t('w18.block.company')} maxLength={120} value={draft.company} onChange={(e) => setDraft({ ...draft, company: e.target.value })} />
        <div>
          <label htmlFor="w18-criteria" className="mb-1 block text-sm font-medium text-slate-800">
            {t('w18.block.criteria')}
          </label>
          <textarea
            id="w18-criteria"
            rows={3}
            maxLength={1000}
            className="block w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
            value={draft.criteria}
            aria-describedby="w18-criteria-hint"
            onChange={(e) => setDraft({ ...draft, criteria: e.target.value })}
          />
          <p id="w18-criteria-hint" className="mt-1 text-xs text-slate-600">
            {t('w18.block.criteriaHint')}
          </p>
        </div>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-slate-800">{t('w18.block.sites')}</legend>
          <CheckboxField label={t('w18.block.allSites')} checked={draft.allSites} onChange={(e) => setDraft({ ...draft, allSites: e.target.checked })} />
          {!draft.allSites ? (
            <div className="grid gap-2 sm:grid-cols-2">
              {(sites.data ?? []).map((s) => (
                <CheckboxField
                  key={s.id}
                  label={s.name}
                  checked={draft.siteIds.includes(s.id)}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      siteIds: e.target.checked ? [...draft.siteIds, s.id] : draft.siteIds.filter((x) => x !== s.id),
                    })
                  }
                />
              ))}
            </div>
          ) : null}
        </fieldset>
        <TextField
          type="date"
          label={t('w18.block.expires')}
          hint={t('w18.block.expiresHint')}
          value={draft.expiresOn}
          error={errors['expiresOn']}
          onChange={(e) => setDraft({ ...draft, expiresOn: e.target.value })}
        />
        {localError ? <Alert>{localError}</Alert> : null}
        <ErrorLine error={save.error} />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="primary" disabled={save.isPending || draft.fullName.trim().length < 2}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function TestCard() {
  const sites = useSiteOptions();
  const [name, setName] = useState('');
  const [siteId, setSiteId] = useState('');
  const test = useMutation({
    mutationFn: () =>
      api.post(`${SAFETY}/test`, {
        body: { fullName: name, siteId: siteId || null },
        schema: BlockTestResult,
      }),
  });
  return (
    <Card title={t('w18.block.test.title')} description={t('w18.block.test.help')}>
      <form
        className="grid gap-3 sm:grid-cols-[1fr_14rem_auto] sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          test.mutate();
        }}
      >
        <TextField label={t('w18.block.test.name')} value={name} onChange={(e) => setName(e.target.value)} />
        <SelectField label={t('w18.block.test.site')} value={siteId} onChange={(e) => setSiteId(e.target.value)}>
          <option value="">{t('w18.block.test.anySite')}</option>
          {(sites.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
        <Button type="submit" disabled={test.isPending || name.trim() === ''}>
          {t('w18.block.test.button')}
        </Button>
      </form>
      <div className="mt-3" aria-live="polite">
        {test.data ? (
          test.data.matched ? (
            <Alert kind="error">
              {t('w18.block.test.matched', {
                count: test.data.entryIds.length,
              })}
            </Alert>
          ) : (
            <Alert kind="success">{t('w18.block.test.clear')}</Alert>
          )
        ) : null}
        <ErrorLine error={test.error} />
      </div>
    </Card>
  );
}

function MatchesCard() {
  const [pageNo, setPageNo] = useState(1);
  const q = useQuery({
    queryKey: ['w18', 'block', 'matches', pageNo],
    queryFn: () =>
      api.get(`${SAFETY}/matches`, {
        query: { page: pageNo, pageSize: 10 },
        schema: BlockMatchList,
      }),
    placeholderData: keepPreviousData,
  });
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  return (
    <section aria-labelledby="w18-matches">
      <h2 id="w18-matches" className="mb-3 text-lg font-semibold text-slate-900">
        {t('w18.block.matches.title')}
      </h2>
      {!q.data ? (
        <Spinner />
      ) : (
        <>
          <TableWrap caption={t('w18.block.matches.title')}>
            <thead className="bg-slate-50">
              <tr>
                <Th>{t('w18.block.matches.when')}</Th>
                <Th>{t('w18.block.matches.name')}</Th>
                <Th>{t('w18.block.matches.outcome')}</Th>
                <Th>{t('w18.block.matches.source')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {q.data.items.length === 0 ? <EmptyRow cols={4} /> : null}
              {q.data.items.map((m) => (
                <tr key={m.id}>
                  <td className="px-4 py-2.5">{formatDateTime(m.occurredAt)}</td>
                  <td className="px-4 py-2.5">{m.name}</td>
                  <td className="px-4 py-2.5">
                    <Badge tone={m.outcome === 'refused' ? 'bad' : 'warn'}>{t(`w18.block.outcome.${m.outcome}`)}</Badge>
                  </td>
                  <td className="px-4 py-2.5">{m.source}</td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <Pager page={pageNo} pageSize={10} total={q.data.total} onPage={setPageNo} />
        </>
      )}
    </section>
  );
}

/**
 * Manage -> Safety check: the block list, "test a name" and recent matches (contract safety-check s5).
 * The reason (criteria) is shown, and can be edited, only with `safety_check.manage`.
 */
export function BlockListPage() {
  const canManage = useCan('safety_check.manage');
  const canDelete = useCan('safety_check.delete');
  const qc = useQueryClient();
  const toast = useToast();
  const list = useListState();
  const [status, setStatus] = useState<Status>('active');
  const [editing, setEditing] = useState<BlockEntry | 'new' | null>(null);
  const sites = useSiteOptions();
  const query = {
    page: list.page,
    pageSize: PAGE_SIZE,
    q: list.search || undefined,
    status,
  };
  const q = useQuery({
    queryKey: ['w18', 'block', 'list', query],
    queryFn: () => api.get(`${SAFETY}/entries`, { query, schema: BlockList }),
    placeholderData: keepPreviousData,
  });
  const patch = useMutation({
    mutationFn: ({ entry, active }: { entry: BlockEntry; active: boolean }) =>
      // The whole entry is sent: PATCH bodies parsed with BlockEntryUpdate get their defaults back (zod v4 partial keeps them), which would blank the other fields.
      api.patch(`${SAFETY}/entries/${entry.id}`, {
        body: { fullName: entry.fullName, aliases: entry.aliases, company: entry.company, criteria: entry.criteria, siteIds: entry.siteIds, expiresOn: entry.expiresOn, active },
        schema: BlockEntry,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['w18', 'block'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`${SAFETY}/entries/${id}`),
    onSuccess: async () => {
      toast(t('w18.block.deleted'));
      await qc.invalidateQueries({ queryKey: ['w18', 'block'] });
    },
  });

  const siteName = (id: string) => sites.data?.find((s) => s.id === id)?.name ?? '';
  return (
    <div>
      <PageHeader
        title={t('w18.block.title')}
        description={t('w18.block.description')}
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setEditing('new')}>
              {t('w18.block.add')}
            </Button>
          ) : undefined
        }
      />
      <div className="space-y-8">
        <TestCard />
        <section aria-labelledby="w18-entries">
          <h2 id="w18-entries" className="mb-3 text-lg font-semibold text-slate-900">
            {t('w18.block.entries')}
          </h2>
          <FilterBar columns="sm:grid-cols-[1fr_12rem]">
            <TextField label={t('common.search')} value={list.search} onChange={(e) => list.setSearch(e.target.value)} />
            <SelectField
              label={t('common.status')}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as Status);
                list.resetPage();
              }}
            >
              <option value="active">{t('w18.block.status.active')}</option>
              <option value="inactive">{t('w18.block.status.inactive')}</option>
              <option value="all">{t('w18.block.status.all')}</option>
            </SelectField>
          </FilterBar>
          {q.isError ? (
            <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
          ) : !q.data ? (
            <Spinner />
          ) : (
            <>
              <TableWrap caption={t('w18.block.entries')}>
                <thead className="bg-slate-50">
                  <tr>
                    <Th>{t('w18.block.name')}</Th>
                    <Th>{t('w18.block.company')}</Th>
                    <Th>{t('w18.block.sites')}</Th>
                    <Th>{t('w18.block.updated')}</Th>
                    <Th>{t('common.status')}</Th>
                    {canManage ? <Th>{t('common.actions')}</Th> : null}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {q.data.items.length === 0 ? <EmptyRow cols={canManage ? 6 : 5} /> : null}
                  {q.data.items.map((e) => (
                    <tr key={e.id}>
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-slate-900">{e.fullName}</div>
                        {e.aliases.length > 0 ? (
                          <div className="text-xs text-slate-600">
                            {t('w18.block.alsoKnown', {
                              names: e.aliases.join(', '),
                            })}
                          </div>
                        ) : null}
                        {canManage && e.criteria ? <div className="mt-1 text-xs text-slate-700">{e.criteria}</div> : null}
                      </td>
                      <td className="px-4 py-2.5">{e.company}</td>
                      <td className="px-4 py-2.5">{e.siteIds === null ? t('w18.block.everySite') : e.siteIds.map(siteName).filter(Boolean).join(', ')}</td>
                      <td className="px-4 py-2.5">
                        <div>{formatDateTime(e.updatedAt)}</div>
                        <div className="text-xs text-slate-600">{e.updatedByLabel}</div>
                        {e.expiresOn ? (
                          <div className="text-xs text-slate-600">
                            {t('w18.block.expiresOn', {
                              date: formatDay(e.expiresOn),
                            })}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge tone={e.active ? 'bad' : 'neutral'}>{e.active ? t('w18.block.status.active') : t('w18.block.status.inactive')}</Badge>
                      </td>
                      {canManage ? (
                        <td className="px-4 py-2.5">
                          <div className="flex flex-wrap gap-2">
                            <Button
                              onClick={() => setEditing(e)}
                              aria-label={t('w18.block.editName', {
                                name: e.fullName,
                              })}
                            >
                              {t('common.edit')}
                            </Button>
                            <Button onClick={() => patch.mutate({ entry: e, active: !e.active })} disabled={patch.isPending}>
                              {e.active ? t('w18.block.deactivate') : t('w18.block.activate')}
                            </Button>
                            {canDelete ? <ConfirmButton label={t('common.delete')} onConfirm={() => remove.mutate(e.id)} pending={remove.isPending} /> : null}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
              <Pager page={list.page} pageSize={PAGE_SIZE} total={q.data.total} onPage={list.setPage} />
            </>
          )}
          <ErrorLine error={patch.error ?? remove.error} />
        </section>
        <MatchesCard />
      </div>
      {editing ? <EntryDialog entry={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}
